import { computed, ref, watch } from "vue";
import { triggerMobileAppEvent, getAllwynAppName } from "@allwyn/mobile-apps/mobile-app-integration";
import { ngFrameworkLoaded } from "@allwyn/ng/ng-integration";
import { useBreakpoints } from "@allwyn/utils/composables/use-breakpoints";
import { useScreenOrientation } from "@allwyn/utils/composables/use-screen-orientation";
import { ResponsiveUtils } from "@allwyn/utils/responsive-utils";
import { CasinoApi } from "./casino-api";
import { NgCasinoAnalytics } from "./ng-casino-analytics";
import { useNgCasinoState } from "./use-ng-casino-state";
import { CasinoGameLoadParams } from "../model/casino-game-load-params";
import { CasinoGameType } from "../model/casino-game-type";

const gameTypeBaseUrls: Record<CasinoGameType, string> = {
    [CasinoGameType.game]: "/hry",
    [CasinoGameType.livedealer]: "/hry-live",
    [CasinoGameType.eticket]: "/losy",
    [CasinoGameType.funpark]: "/funpark",
    [CasinoGameType.underground]: "/losy/stiraci-hry",
};

const GAME_REDIRECT_BASE_URL = "/spustit-hru";

let isAlreadyClosing = false;
const isDepositOpen = ref(false);
const { currentGameServerData, returnUrl } = useNgCasinoState();
const sm2 = useBreakpoints("sm2");
const isMobile = computed(() => !sm2.value);
const shouldBlockPanicButtonTemporarily = ref(false);

const { isLandscape } = useScreenOrientation();

const isSidebarLayout = computed(() => !isMobile.value || isLandscape.value);
const lastKnownGameType = ref(currentGameServerData.value?.type);

watch(
    () => currentGameServerData.value?.type,
    (newType) => {
        if (newType) {
            lastKnownGameType.value = newType;
        }
    }
);

async function startDeposit(codeName: string) {
    NgCasinoAnalytics.pushIframeDepositClick(codeName);

    if (environment?.webView?.isAppWebView) {
        const deeplink =
            environment.appName === "sazkabet" || environment.appName === "sazkahry"
                ? `${getAllwynAppName()}://home/widget/deposit`
                : `${getAllwynAppName()}://widget/deposit`;
        triggerMobileAppEvent("goToDeepLink", deeplink);
        return;
    }

    await ngFrameworkLoaded();
    ng_fw.showDeposit();
    isDepositOpen.value = true;
    if (isMobile.value) shouldBlockPanicButtonTemporarily.value = true;

    ng_hs.depositAborted(() => {
        isDepositOpen.value = false;
        shouldBlockPanicButtonTemporarily.value = false;
    });

    ng_hs.depositSucceeded(() => {
        isDepositOpen.value = false;
        shouldBlockPanicButtonTemporarily.value = false;
    });
}

async function openFullscreenMode() {
    if (!document.fullscreenElement) {
        try {
            await document.documentElement.requestFullscreen({ navigationUI: "hide" });
        } catch (error) {
            console.error("Failed to enter fullscreen mode:", error);
        }
    }
}

function handleFullscreen() {
    if (ResponsiveUtils.isMobile() && !ResponsiveUtils.isIos()) {
        openFullscreenMode();
    }
}

function isValidReturnUrl(url: string | null): url is string {
    return typeof url === "string" && url.startsWith("/") && !url.startsWith("//");
}

function handleCloseNgGame() {
    if (environment?.webView?.isAppWebView) {
        ng_fw.closeGame();
        return;
    }

    if (isAlreadyClosing) return;
    isAlreadyClosing = true;

    const url = isValidReturnUrl(returnUrl.value) ? returnUrl.value : gameTypeBaseUrls[lastKnownGameType.value] ?? "/";
    window.location.href = url;

    isAlreadyClosing = false;
}

function constructGameLaunchUrl(gameLoadParams: CasinoGameLoadParams) {
    const { gameUrl, gameId, moneyMode, withAbTest } = gameLoadParams;

    if (!gameId && !gameUrl) {
        return null;
    }

    const baseUrl = gameUrl ? gameUrl : GAME_REDIRECT_BASE_URL + `/${gameId}`;
    const url = new URL(baseUrl, window.location.origin);

    if (typeof moneyMode === "boolean") {
        url.searchParams.set("moneyMode", moneyMode ? "true" : "false");
    }

    if (withAbTest === false) {
        url.searchParams.set("abTest", "false");
    }

    const returnUrlValue = returnUrl.value ?? window.location.pathname + window.location.search;
    if (isValidReturnUrl(returnUrlValue)) {
        url.searchParams.set("returnUrl", returnUrlValue);
    }

    return url.toString();
}

function getCasinoGameTypeFromUrl(url: string): CasinoGameType | undefined {
    const pathname = url.startsWith("/") ? url : new URL(url).pathname;

    for (const [gameType, baseUrl] of Object.entries(gameTypeBaseUrls)) {
        if (pathname.startsWith(baseUrl)) {
            return gameType as CasinoGameType;
        }
    }

    return undefined;
}

async function getAppCasinoDeepLink(gameLoadParams: CasinoGameLoadParams): Promise<string> {
    const mode = gameLoadParams.moneyMode === true ? "money" : "fun";

    let gameType: CasinoGameType;
    if (gameLoadParams.gameUrl) {
        gameType = getCasinoGameTypeFromUrl(gameLoadParams.gameUrl);
    } else {
        gameType = await getCasinoGameTypeFromApi(gameLoadParams.gameId.toString(), gameLoadParams.withAbTest ?? true);
    }

    const { areGameLayerFeaturesHiddenInApp } = useNgCasinoState();

    if (environment.appName === "sazka") {
        let appGameType: string;
        if (gameType === CasinoGameType.eticket) {
            appGameType = "e-scratches";
        } else if (gameType === CasinoGameType.funpark && !areGameLayerFeaturesHiddenInApp.value) {
            appGameType = "games/funpark";
        } else {
            appGameType = "casino/games";
        }
        return `${getAllwynAppName()}://${appGameType}/${gameLoadParams.gameId}?mode=${mode}`;
    } else if (environment.appName === "sazkahry") {
        const appGameType = gameType === CasinoGameType.livedealer ? "gameslive" : "games";
        return `${getAllwynAppName()}://home/${appGameType}/${gameLoadParams.gameId}?mode=${mode}`;
    }
}

async function getCasinoGameTypeFromApi(gameId: string, withAbTest: boolean): Promise<CasinoGameType | undefined> {
    const gameDetail = await CasinoApi.getGameDetail(gameId, withAbTest);
    return gameDetail ? gameDetail.type : undefined;
}

export function useNgCasinoUtils() {
    return {
        isMobile,
        shouldBlockPanicButtonTemporarily,
        isLandscape,
        isSidebarLayout,
        isDepositOpen,
        startDeposit,
        openFullscreenMode,
        handleFullscreen,
        handleCloseNgGame,
        constructGameLaunchUrl,
        getAppCasinoDeepLink,
    };
}
